import { useBrowserNavigation } from '@marketplace-web/browser/browser-navigation-util'
import { useLatestCallback } from '@marketplace-web/shared/ui-state-util'

import { toParams, toUrlQuery } from '@marketplace-web/browser/url-util'
import {
  buildCatalogUrlParams,
  transformDynamicFilters,
  transformSelectedDynamicFilters,
  FilterModel,
  SelectedDynamicFilterModel,
  PriceRange,
  getCatalogFilters,
  GetCatalogFiltersArgs,
} from '@marketplace-web/catalog/catalog-data'
import {
  useCatalogContext,
  DynamicFilterReducerAction,
  FilterReducerAction,
  GeneralReducerAction,
} from '@marketplace-web/catalog/catalog-page-data'

import useFetchDynamicFiltersFacets from './useFetchDynamicFiltersFacets'

import { CATALOG_URL } from '../constants/routes'

type FetchDynamicFiltersParams = {
  selectedDynamicFilters: Array<SelectedDynamicFilterModel>
  selectedFilters?: Partial<FilterModel>
  isTemporary?: boolean
  selectedTemporaryCatalogIds?: Array<number>
  selectedTemporaryPriceRange?: PriceRange
  isSearchTextOnly?: boolean
}

const useFetchDynamicFilters = () => {
  const { state, dispatch, refs } = useCatalogContext()
  const fetchDynamicFilterFacets = useFetchDynamicFiltersFacets()
  const {
    urlParams,
    searchParams: { search_text },
  } = useBrowserNavigation()
  const searchQuery = search_text || ''

  const fetchDynamicFilters = useLatestCallback(
    ({
      selectedDynamicFilters,
      selectedFilters,
      isTemporary = false,
      selectedTemporaryCatalogIds,
      selectedTemporaryPriceRange,
      isSearchTextOnly,
    }: FetchDynamicFiltersParams) => {
      let payload: GetCatalogFiltersArgs
      const { temporaryPriceRange, temporaryCatalogIds } = state.dynamicFilters
      const filters = {
        ...state.filters,
        ...selectedFilters,
        query: String(searchQuery),
      }
      const time = urlParams.time && Number(urlParams.time)

      dispatch({
        type: FilterReducerAction.CHANGE_FILTERS,
        payload: {
          filters,
        },
      })

      if (isTemporary) {
        const currentTemporaryCatalogIds = selectedTemporaryCatalogIds ?? temporaryCatalogIds
        const currentTemporaryPriceRange = selectedTemporaryPriceRange ?? temporaryPriceRange

        dispatch({
          type: DynamicFilterReducerAction.SET_TEMPORARY_SELECTED_DYNAMIC_FILTERS,
          payload: { temporarySelectedDynamicFilters: selectedDynamicFilters },
        })

        dispatch({
          type: DynamicFilterReducerAction.SET_TEMPORARY_CATALOG_IDS,
          payload: { temporaryCatalogIds: currentTemporaryCatalogIds },
        })

        dispatch({
          type: DynamicFilterReducerAction.SET_TEMPORARY_PRICE_RANGE,
          payload: { priceRange: currentTemporaryPriceRange },
        })

        payload = {
          ...filters,
          selectedDynamicFilters,
          catalogIds: currentTemporaryCatalogIds,
          priceFrom: currentTemporaryPriceRange.priceFrom,
          priceTo: currentTemporaryPriceRange.priceTo,
          time,
        }
      } else {
        dispatch({
          type: DynamicFilterReducerAction.SET_SELECTED_DYNAMIC_FILTERS,
          payload: { selectedDynamicFilters },
        })

        payload = {
          ...filters,
          selectedDynamicFilters,
          time,
        }
      }

      const extraParams = toParams(window.location.search)
      const filterParams = buildCatalogUrlParams({
        filters,
        selectedDynamicFilters,
        extraParams,
      })
      const updatedUrl = `${CATALOG_URL}?${toUrlQuery(filterParams)}`

      let requestVersion = 0
      if (refs.dynamicFilterRequestVersion?.current != null) {
        refs.dynamicFilterRequestVersion.current += 1
        requestVersion = refs.dynamicFilterRequestVersion.current
      }

      const pendingFacetType = refs.pendingFacetsType.current
      refs.isFiltersFetching.current = true

      dispatch({
        type: DynamicFilterReducerAction.FETCH_DYNAMIC_FILTERS_REQUEST,
        payload: undefined,
      })

      getCatalogFilters(payload)
        .then(response => {
          if (requestVersion !== refs.dynamicFilterRequestVersion?.current) {
            refs.isFiltersFetching.current = false

            return
          }

          if ('errors' in response) {
            dispatch({
              type: DynamicFilterReducerAction.FETCH_DYNAMIC_FILTERS_FAILURE,
              payload: { errors: response.errors },
            })
          } else if (isTemporary) {
            dispatch({
              type: DynamicFilterReducerAction.FETCH_TEMPORARY_DYNAMIC_FILTERS_SUCCESS,
              payload: {
                dynamicFilters: transformDynamicFilters(response.filters),
                temporarySelectedDynamicFilters: transformSelectedDynamicFilters(
                  response.selected_filters,
                ),
              },
            })
          } else {
            dispatch({
              type: DynamicFilterReducerAction.FETCH_DYNAMIC_FILTERS_SUCCESS,
              payload: {
                dynamicFilters: transformDynamicFilters(response.filters),
                selectedDynamicFilters: transformSelectedDynamicFilters(response.selected_filters),
              },
            })

            dispatch({
              type: GeneralReducerAction.INCREMENT_FILTERS_CHANGE_COUNT,
              payload: undefined,
            })

            if (isSearchTextOnly) {
              // search text is mostly updated with pushState, so pushing it again
              // would mean duplicate history stack entries
              window.history.replaceState(undefined, '', updatedUrl)
            } else {
              window.history.pushState(undefined, '', updatedUrl)
            }

            // refetch pending facets with new state
            if (pendingFacetType) {
              fetchDynamicFilterFacets({
                selectedDynamicFilters: transformSelectedDynamicFilters(response.selected_filters),
                filterCode: pendingFacetType,
                isLazy: true,
              })
              refs.pendingFacetsType.current = null
            }
          }
        })
        .finally(() => {
          refs.isFiltersFetching.current = false
        })
    },
  )

  return fetchDynamicFilters
}

export default useFetchDynamicFilters
